import React, { useCallback, useEffect, useState } from 'react';
import {
  ScrollView,
  StyleSheet,
  Text,
  View
} from 'react-native';
import Ionicons from 'react-native-vector-icons/Ionicons';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../api/client';
import { getThemeColors } from '../utils/themeStyles';
import ErrorRetry from '../components/ErrorRetry';
import ActionLoader from '../components/ActionLoader';
import { useT } from '../i18n/useT';

// Moyen de paiement REEL, derive de l'historique payment.method (cf. backend
// GET /me/payment-methods). Pas de carte enregistree : on affiche ce qui a
// effectivement servi a payer (lecture seule).
interface PaymentMethod {
  id: string;
  method: string | null;
  label: string;
  brand: string;
  last4: string | null;
  count: number;
  total: number;
  lastUsedAt: string | null;
}

const BRAND_STYLE: Record<string, { bg: string; short: string }> = {
  visa: { bg: '#1e3a8a', short: 'VISA' },
  mastercard: { bg: '#7f1d1d', short: 'MC' },
  amex: { bg: '#1f6f6f', short: 'AMEX' },
  paypal: { bg: '#1e3a5f', short: 'PP' },
  stripe: { bg: '#4338CA', short: 'STR' },
  mollie: { bg: '#0D3D66', short: 'MOL' },
  simulation: { bg: '#475569', short: 'SIM' },
  free: { bg: '#065F46', short: 'FREE' },
  other: { bg: '#374151', short: '••' },
};

export default function PaymentMethodsScreen() {
  const t = useT();
  const { isDarkTheme } = useTheme();
  const { user, getAccessToken } = useAuth();
  const colors = getThemeColors(isDarkTheme);

  const [methods, setMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Chargement DB : GET /me/payment-methods (utilisateur connecté).
  const loadMethods = useCallback(async () => {
    const token = getAccessToken();
    if (!user || !token) { setMethods([]); setError(false); setLoading(false); return; }
    setLoading(true);
    try {
      const data = await apiFetch<{ methods: PaymentMethod[] }>('/me/payment-methods', { token, auth: true });
      setMethods(data?.methods ?? []);
      setError(false);
    } catch (_) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [user, getAccessToken]);

  useEffect(() => { void loadMethods(); }, [loadMethods]);

  const formatDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString() : '—');

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]}>
      <View style={[styles.header, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
        <Text style={[styles.headerTitle, { color: colors.textPrimary }]}>{t('payment.title')}</Text>
        <Text style={[styles.headerSubtitle, { color: colors.textSecondary }]}>{t('payment.subtitle')}</Text>
      </View>

      <ScrollView style={[styles.content, { backgroundColor: colors.background }]} showsVerticalScrollIndicator={false}>
        <View style={styles.section}>
          <Text style={[styles.sectionTitle, { color: colors.textPrimary }]}>{t('payment.history')}</Text>

          {error && methods.length === 0 ? (
            <ErrorRetry onRetry={() => void loadMethods()} retrying={loading} message={t('payment.error')} />
          ) : loading && methods.length === 0 ? (
            <View style={[styles.emptyState, { backgroundColor: colors.cardBackground }]}>
              <ActionLoader label={t('payment.loading')} />
            </View>
          ) : methods.length === 0 ? (
            <View style={[styles.emptyState, { backgroundColor: colors.cardBackground }]}>
              <Ionicons name="card-outline" size={48} color={colors.textTertiary} />
              <Text style={[styles.emptyText, { color: colors.textSecondary }]}>{t('payment.empty')}</Text>
            </View>
          ) : (
            methods.map((m) => {
              const brand = BRAND_STYLE[m.brand] || BRAND_STYLE.other;
              return (
                <View key={m.id} style={styles.cardContainer}>
                  <View style={[styles.visualCard, { backgroundColor: brand.bg }]}>
                    <View style={styles.cardTopRow}>
                      <View style={styles.chip}>
                        <View style={styles.chipLine} />
                        <View style={styles.chipLine} />
                      </View>
                      <View style={styles.cardTypeIcon}>
                        <Text style={styles.cardTypeText}>{brand.short}</Text>
                      </View>
                    </View>
                    <Text style={styles.cardNumber}>{m.last4 ? `•••• •••• •••• ${m.last4}` : m.label}</Text>
                    <View style={styles.cardBottomRow}>
                      <View>
                        <Text style={styles.cardLabel}>{t('payment.payments')}</Text>
                        <Text style={styles.cardValue}>{m.count}</Text>
                      </View>
                      <View>
                        <Text style={styles.cardLabel}>{t('payment.totalSpent')}</Text>
                        <Text style={styles.cardValue}>${m.total.toFixed(2)}</Text>
                      </View>
                      <View>
                        <Text style={styles.cardLabel}>{t('payment.last')}</Text>
                        <Text style={styles.cardValue}>{formatDate(m.lastUsedAt)}</Text>
                      </View>
                    </View>
                  </View>
                </View>
              );
            })
          )}
        </View>

        <View style={[styles.noticeCard, { backgroundColor: colors.cardBackground }]}>
          <Ionicons name="information-circle-outline" size={18} color={colors.iconSecondary} />
          <Text style={[styles.noticeText, { color: colors.textSecondary }]}>
            {t('payment.notice')}
          </Text>
        </View>

        <View style={{ height: 32 }} />
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  header: { padding: 16, borderBottomWidth: 1 },
  headerTitle: { fontSize: 24, fontWeight: 'bold', marginBottom: 4 },
  headerSubtitle: { fontSize: 14 },
  content: { flex: 1 },
  section: { padding: 16 },
  sectionTitle: { fontSize: 18, fontWeight: 'bold', marginBottom: 16 },
  emptyState: { borderRadius: 12, padding: 32, alignItems: 'center', gap: 12 },
  emptyText: { fontSize: 14 },
  noticeCard: { flexDirection: 'row', alignItems: 'flex-start', gap: 10, marginHorizontal: 16, padding: 14, borderRadius: 12 },
  noticeText: { flex: 1, fontSize: 12, lineHeight: 18 },
  cardContainer: { marginBottom: 16 },
  visualCard: { borderRadius: 16, padding: 20, marginBottom: 8, minHeight: 180 },
  cardTopRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 },
  chip: { width: 40, height: 30, borderRadius: 4, backgroundColor: '#D4AF37', padding: 4, justifyContent: 'space-around' },
  chipLine: { height: 2, backgroundColor: 'rgba(0,0,0,0.3)', borderRadius: 1 },
  cardTypeIcon: { paddingHorizontal: 8, paddingVertical: 4, borderRadius: 4 },
  cardTypeText: { color: '#FFFFFF', fontWeight: 'bold', fontSize: 12, letterSpacing: 1 },
  cardNumber: { color: '#FFFFFF', fontSize: 20, fontWeight: '600', letterSpacing: 3, marginBottom: 20, fontFamily: 'monospace' },
  cardBottomRow: { flexDirection: 'row', justifyContent: 'space-between' },
  cardLabel: { color: 'rgba(255,255,255,0.7)', fontSize: 10, textTransform: 'uppercase', marginBottom: 2 },
  cardValue: { color: '#FFFFFF', fontSize: 14, fontWeight: '600' },
  removeButton: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', borderWidth: 1, borderRadius: 8, paddingVertical: 8, gap: 6 },
  removeButtonText: { fontSize: 14, fontWeight: '500' },
  addCardButton: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', marginHorizontal: 16, paddingVertical: 14, borderRadius: 12, gap: 8 },
  addCardButtonText: { color: '#FFFFFF', fontSize: 16, fontWeight: '600' },
  formCard: { margin: 16, borderRadius: 16, padding: 20, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 8, elevation: 4 },
  formTitle: { fontSize: 18, fontWeight: 'bold', marginBottom: 20 },
  field: { marginBottom: 16 },
  fieldRow: { flexDirection: 'row', gap: 12 },
  label: { fontSize: 12, fontWeight: '600', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 },
  inputContainer: { flexDirection: 'row', alignItems: 'center', borderWidth: 1, borderRadius: 10, paddingHorizontal: 12, gap: 8 },
  input: { flex: 1, paddingVertical: 12, fontSize: 16 },
  formButtons: { flexDirection: 'row', gap: 12, marginTop: 8 },
  cancelButton: { flex: 1, borderWidth: 1, borderRadius: 10, paddingVertical: 12, alignItems: 'center' },
  cancelButtonText: { fontSize: 15, fontWeight: '500' },
  saveButton: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', borderRadius: 10, paddingVertical: 12, gap: 6 },
  saveButtonText: { color: '#FFFFFF', fontSize: 15, fontWeight: '600' },
});
